import { useEffect, useMemo, useState } from "react";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";

import { checkoutStripe, checkoutStripeLifecycle } from "../lib/stripeCheckout";
import { InlineError, Skeleton } from "../ui";

export function CheckoutPayment({
  clientSecret,
  publishableKey,
}: {
  clientSecret: string;
  publishableKey: string;
}) {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [failed, setFailed] = useState(false);
  const options = useMemo(() => ({ clientSecret }), [clientSecret]);
  useEffect(() => {
    let mounted = true;
    const lifecycle = checkoutStripeLifecycle(() => {
      setFailed(true);
    });
    const promise = checkoutStripe(publishableKey).then((stripe) =>
      stripe && mounted ? lifecycle.wrap(stripe) : null,
    );
    void promise.then((stripe) => {
      if (!mounted) return;
      if (!stripe) setFailed(true);
      else setStripePromise(promise);
    });
    return () => {
      mounted = false;
      lifecycle.dispose();
    };
  }, [publishableKey]);
  if (failed) {
    return (
      <InlineError>
        Unable to load payment. Check your connection, then choose Change and
        try again.
      </InlineError>
    );
  }
  if (!stripePromise)
    return <Skeleton shape="detail-field" label="Loading payment" rows={4} />;
  return (
    <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
}
